import { Box } from '@mui/material';
import { MuiButtonBase } from '@ui/base_library';
import { makeStyles } from 'tss-react/mui';
import React, { useCallback, useMemo } from 'react';
import Typography from '@ui/Typography';
import AvatarWithFallback from '@avatar/components/AvatarWithFallback';
import Icon from '@ui/Icon';
import type { Actor as GeneratedAvatar } from '@avatar/types';
import { ActorVariant as GeneratedAvatarVariant } from '../../../../../../server/graphql/__generated.types';
import {
  getCountryNameSluggified,
  getVariantLabel,
  mapLocale,
} from '../../../utils/avatar';

const useStyles = makeStyles<{
  isActive: boolean;
  forceActiveStyle: boolean;
  projectVoiceVariant: boolean;
}>()((theme, { isActive, forceActiveStyle, projectVoiceVariant }) => ({
  root: {
    alignItems: 'center',
    display: 'flex',
    minHeight: theme.spacing(5),
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(0.25),
    textAlign: 'left',
    transition: theme.transitions.create('opacity'),
    width: '100%',
    borderRadius: theme.spacing(0.5),
    '&.inactive': {
      opacity: !forceActiveStyle ? 0.5 : 1,
      '&:hover': {
        opacity: 1,
      },
    },
    '&:active': {
      opacity: !forceActiveStyle ? `0.75 !important` : 1,
    },
    '&.Mui-focusVisible': {
      color: theme.palette.common.white,
      opacity: `1 !important`,
      borderRadius: theme.spacing(0.5),
      // Adjust spacing so focusing from variant to icon button looks better
      width: `calc(100% - ${theme.spacing(2)})`,
    },
  },
  avatarContainer: {
    border: '2px solid transparent',
    borderColor: isActive ? theme.palette.primary.main : 'transparent',
    borderRadius: '50%',
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(-0.25),
    padding: theme.spacing(0.25),
    position: 'relative',
  },
  avatar: {
    height: theme.spacing(4),
    width: theme.spacing(4),
  },
  lockIcon: {
    marginLeft: theme.spacing(1),
    display: 'block',
    transform: 'scale(0.80)',
  },
  avatarName: {
    display: 'flex',
    flexDirection: 'row',
  },
  variantLabel: {
    textTransform: 'capitalize',
  },
  projectVoiceVariant: {
    outline: !isActive
      ? `1px solid ${theme.palette.grey[800]}`
      : `1px solid ${theme.palette.primary.main}`,
    padding: `8px 12px`,
    backgroundColor: !isActive ? 'unset' : theme.palette.grey[900],
    '&:hover': {
      // Could use var(--color-gray-1050) bc doesn't exist in palettev2
      // but leaving as hexcode to appease typechecking
      backgroundColor: `#191919 !important`,
    },
  },
}));

export interface AvatarVariantListItemProps {
  avatar: GeneratedAvatar;
  isActive: boolean;
  variant: GeneratedAvatarVariant;
  onClick: (avatar: GeneratedAvatar) => void;
  disabled?: boolean | null;
  /**
   * What the row announces to assistive tech. Defaults to the select action
   * every list of avatars has described so far; lists whose rows do something
   * else (such as opening a replace popout) pass their own wording.
   */
  label?: string;
  /**
   * True if opacity should be on
   * Use `isActive` for active border on avatar
   */
  forceActiveStyle?: boolean;
  projectVoiceVariant?: boolean;
  rootClassName?: string;
  focusVisibleClassName?: string;
}

const useLocaleFlagStyles = makeStyles()(theme => ({
  localeFlagLabelContainer: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  flagContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'center',
    flexShrink: 0,
    lineHeight: 0,
  },
  flag: {
    width: 16,
    height: 16,
    display: 'block',
  },
  localeContainer: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
  },
}));

function LocaleFlagLabel({
  avatar,
  variant,
}: {
  avatar: GeneratedAvatar;
  variant: GeneratedAvatarVariant;
}) {
  const { classes } = useLocaleFlagStyles();

  const flag = avatar.variants?.length
    ? getCountryNameSluggified(avatar)
    : null;

  const languageLabel = mapLocale(variant.locale);
  return (
    <div className={classes.localeFlagLabelContainer}>
      <span className={classes.flagContainer}>
        <img
          src={`/static/images/flags/${flag}.svg`}
          alt={flag ?? undefined}
          className={classes.flag}
        />
      </span>
      <div title={languageLabel} className={classes.localeContainer}>
        <Typography
          variant="pSm"
          color="secondary"
          aria-label={`dialects: ${languageLabel}`}
        >
          {languageLabel}
        </Typography>
      </div>
    </div>
  );
}

export default function AvatarVariantListItem(
  props: AvatarVariantListItemProps
) {
  const {
    avatar,
    variant,
    isActive,
    onClick,
    disabled,
    label,
    forceActiveStyle = false,
    projectVoiceVariant = false,
    rootClassName,
    focusVisibleClassName,
  } = props;
  const { classes, cx } = useStyles({
    isActive,
    forceActiveStyle: forceActiveStyle || projectVoiceVariant,
    projectVoiceVariant,
  });

  const onClickCallback = useCallback(() => {
    onClick(avatar);
  }, [avatar, onClick]);

  const variantLabel = useMemo<string>(
    () => getVariantLabel(avatar, variant)?.toLowerCase(),
    [avatar, variant]
  );

  return (
    <Box position="relative">
      <MuiButtonBase
        component="div"
        role="button"
        aria-label={label ?? `Select avatar ${avatar.name}`}
        className={cx(classes.root, {
          inactive: !isActive,
          [classes.projectVoiceVariant]: projectVoiceVariant,
        })}
        onClick={onClickCallback}
        disabled={!!disabled}
        classes={{
          root: rootClassName,
          focusVisible: focusVisibleClassName,
        }}
      >
        <Box className={classes.avatarContainer}>
          <AvatarWithFallback
            className={classes.avatar}
            src={avatar.image}
            alt={avatar.name}
            isFavorite={Boolean(variant.userSettings?.favoritedAt)}
          />
        </Box>
        <Box flexGrow={1} overflow="hidden" display="flex" flexDirection="row">
          <Box position="relative" display="flex" flexDirection="column">
            <Typography
              variant="pMd"
              color="primary"
              className={classes.avatarName}
              bold
            >
              {avatar.name}
            </Typography>
            <Box>
              {avatar.accessLevel === 'LOCKED' && (
                <Icon
                  type="lock"
                  className={classes.lockIcon}
                  color="disabled"
                  filled
                />
              )}
            </Box>
            <Typography
              variant="pSm"
              color="secondary"
              className={classes.variantLabel}
            >
              {variantLabel}
            </Typography>
            {projectVoiceVariant && (
              <LocaleFlagLabel avatar={avatar} variant={variant} />
            )}
          </Box>
        </Box>
        {projectVoiceVariant && (
          <Box display="flex">
            <Icon type="pencil01" />
          </Box>
        )}
      </MuiButtonBase>
    </Box>
  );
}
